"use client"

import Link from 'next/link'
import { usePathname } from 'next/navigation'

type Crumb = { label: string; href: string }

const labels: Record<string, string> = {
  about: '소개',
  programs: '프로그램',
  therapist: '음악치료사',
  community: '커뮤니티',
  free: '자유게시판',
  'field-story': '현장 이야기',
  notices: '공지사항',
  jobs: '취업자료',
  resources: '자료실',
  blog: '블로그',
  roadmap: '개발순서',
  contact: '문의하기',
  privacy: '개인정보처리방침',
  admin: '관리자',
  inquiries: '문의 관리',
  login: '로그인',
  auth: '회원',
  signup: '회원가입',
  'senior-center-music-therapy': '노인복지센터 음악치료',
  'group-music-therapy': '그룹 음악치료',
  'individual-music-therapy': '개인 음악치료',
  child: '아동 프로그램',
  youth: '청소년 프로그램',
  adult: '성인 프로그램',
  senior: '시니어 프로그램',
  institution: '기관 프로그램',
}

// Breadcrumb roots follow the primary navigation grouping (소개 / 요양병원찾기 / 블로그 / 커뮤니티 / 문의하기)
// rather than the raw URL path, since several pages (음악치료사, 공지사항) live under 소개 in the menu
// but not under /about in the URL.
const overrides: Record<string, Crumb[]> = {
  '/about': [{ label: '소개', href: '/about' }],
  '/therapist': [{ label: '소개', href: '/about' }, { label: '음악치료사', href: '/therapist' }],
  '/notices': [{ label: '소개', href: '/about' }, { label: '공지사항', href: '/notices' }],
  '/jobs/institutions/map': [{ label: '요양병원찾기', href: '/jobs/institutions/map' }],
  '/blog': [{ label: '블로그', href: '/blog' }],
  '/community': [{ label: '커뮤니티', href: '/community' }],
  '/community/free': [{ label: '커뮤니티', href: '/community' }, { label: '자유게시판', href: '/community/free' }],
  '/community/field-story': [{ label: '커뮤니티', href: '/community' }, { label: '현장 이야기', href: '/community/field-story' }],
  '/community/jobs': [{ label: '커뮤니티', href: '/community' }, { label: '취업정보', href: '/community/jobs' }],
  '/community/news': [{ label: '커뮤니티', href: '/community' }, { label: '음악치료소식', href: '/community/news' }],
  '/contact': [{ label: '문의하기', href: '/contact' }],
}

function getLabel(segment: string, index: number, segments: string[]) {
  if (labels[segment]) return labels[segment]
  if (segments[index - 1] === 'notices') return '글보기'
  if (segments[index - 1] === 'free' && segment !== 'edit') return '글보기'
  if (segments[index - 1] === 'field-story' && segment !== 'edit') return '글보기'
  if (segments[index - 1] === 'blog' && segment !== 'edit') return '글보기'
  if (segment === 'edit') return '글수정'
  return decodeURIComponent(segment)
}

function getOverrideTrail(pathname: string): Crumb[] | null {
  const matchedKey = Object.keys(overrides)
    .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0]
  return matchedKey ? overrides[matchedKey] : null
}

export function Breadcrumbs() {
  const pathname = usePathname()
  if (!pathname || pathname === '/') return null
  const segments = pathname.split('/').filter(Boolean)
  const overrideTrail = getOverrideTrail(pathname)
  const overrideSegmentCount = overrideTrail ? overrideTrail[overrideTrail.length - 1].href.split('/').filter(Boolean).length : 0
  const crumbs: Crumb[] = overrideTrail ? [...overrideTrail] : []
  segments.slice(overrideSegmentCount).forEach((segment, offset) => {
    const index = overrideSegmentCount + offset
    const href = `/${segments.slice(0, index + 1).join('/')}`
    crumbs.push({ label: getLabel(segment, index, segments), href })
  })

  return <nav aria-label="breadcrumb" className="border-b border-[#D1D5DB] bg-[#F3F4F6] px-4 py-3 text-sm font-bold text-[#4B5563] sm:px-5">
    <ol className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
      <li><Link href="/" className="text-[#111827] transition hover:text-[#FF4500]">홈</Link></li>
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1
        return <li key={crumb.href} className="flex items-center gap-2">
          <span className="text-[#9CA3AF]">/</span>
          {isLast ? <span className="text-[#FF4500]">{crumb.label}</span> : <Link href={crumb.href} className="transition hover:text-[#FF4500]">{crumb.label}</Link>}
        </li>
      })}
    </ol>
  </nav>
}
